import { AnalyticsEventName } from '@dupe/common/constants'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import BeforeAfterSlider from '~/src/components/BeforeAfterSlider'
import RestyleUploadButton from '~/src/components/restyle/RestyleUploadButton'
import SparklesIcon from '~/src/components/svg/SparklesIcon'
import { DialogContent, DialogTitle } from '~/src/components/ui/dialog'
import { Dialog } from '~/src/components/ui/dialog'
import { useAuthContext } from '~/src/context/AuthProvider'
import { useSearchContext } from '~/src/context/SearchProvider'
import useAnalytics from '~/src/hooks/useAnalytics'
import { cn } from '~/src/lib/utils'

interface Props extends React.PropsWithChildren {
  openImagePicker: ({
    source,
  }: {
    source?: 'dupe-input-camera' | 'dupe-input-upload' | 'dupe-input-restyle'
  }) => void
  setDropdownOpen?: Dispatch<SetStateAction<boolean>>
  className?: string
}

const RestyleDialog: React.FC<Props> = ({
  openImagePicker,
  setDropdownOpen,
  children,
  className,
}) => {
  const analytics = useAnalytics()
  const { processing, fileData } = useSearchContext()
  const { isOpen: authModalOpen } = useAuthContext()
  const [isOpen, setIsOpen] = useState(false)

  const onRestylePress = () => {
    analytics.track(AnalyticsEventName.ClickedInputMedia, {
      media: 'restyle',
    })

    openImagePicker({
      source: 'dupe-input-restyle',
    })

    if (setDropdownOpen) {
      setDropdownOpen(false)
    }
  }

  useEffect(() => {
    if (!fileData) return

    const timer = setTimeout(() => {
      if (authModalOpen) {
        setIsOpen(false)
      }
    }, 500)

    return () => clearTimeout(timer)
  }, [fileData, setIsOpen, authModalOpen])

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      {children}

      <DialogContent
        className={cn(
          'max-w-sm rounded-2xl border-none bg-[#1D1D1D] p-0 text-white md:max-w-md',
          className
        )}
      >
        <div className='relative p-6 pt-8'>
          <div className='flex flex-col gap-2'>
            <DialogTitle className='flex items-center gap-2'>
              <SparklesIcon className='h-7 w-7' />
              <span className='text-2xl font-semibold'>Restyle your room</span>
            </DialogTitle>
            <div className='opacity-60 md:text-lg'>
              Upload a well-lit room photo and let AI transform your space like
              a personal designer.
            </div>
          </div>
        </div>
        <div className='relative aspect-[4/3] w-full'>
          <BeforeAfterSlider
            originalImage={{
              imageUrl: '/assets/restyle/examples/before.jpg',
              alt: '',
            }}
            restyledImage={{
              imageUrl: '/assets/restyle/examples/after.jpg',
              alt: '',
            }}
            originalLabel='Original'
            restyledLabel='Restyled'
          />
        </div>

        <div className='p-6 pb-8'>
          <RestyleUploadButton
            onClick={onRestylePress}
            processing={processing}
            className='w-full'
          />
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default RestyleDialog
